// Basic diet plan from weight, height, an age range, (optionally) biological sex and a target
// weight: daily calories, macros and a few micronutrients, every value rounded UP to the next
// whole number. A starting point, not medical advice. Without a sex, formulas use the midpoint
// between the male and female versions, and micronutrients the higher of the two.
//
// Energy is built from absolute parts instead of one activity factor (PAL), because a factor
// scales everything with the resting rate and lumps walking, work and sport into one guess:
//   rest (Mifflin–St Jeor) + daily life awake + walking from steps + sport minutes, then
//   digestion (thermic effect of food, ≈ 10 % of the total).
// Activities use MET values of the Compendium of Physical Activities, applied to the person's
// own resting rate (1 MET = BMR / 24 per hour) instead of the textbook 1 kcal/kg/h, which
// overestimates rest for most people (Byrne 2005). Only what is above rest is added:
// (MET − 1) × BMR/24 × hours. Sources are listed in the app (Info → How the diet plan works).
import { DIETS, type Diet } from '../types';

/** Age ranges of the reference intakes. Calories use the age in the middle (`AGE_MIDDLE`). */
export const AGE_RANGES = ['14-18', '19-30', '31-50', '51-70', '71+'] as const;
export type AgeRange = (typeof AGE_RANGES)[number];
export const AGE_MIDDLE: Record<AgeRange, number> = { '14-18': 16, '19-30': 25, '31-50': 40, '51-70': 60, '71+': 75 };
export function ageRangeOf(years: number): AgeRange {
  return years < 19 ? '14-18' : years < 31 ? '19-30' : years < 51 ? '31-50' : years < 71 ? '51-70' : '71+';
}

export const SEXES = ['female', 'male'] as const;
export type Sex = (typeof SEXES)[number];

/** Sport, by Compendium MET. Minutes a week are asked per sport. */
export const SPORT_MET = {
  strength: 3.5, // resistance training, multiple exercises, 8–15 reps (2024 Compendium 02054)
  yoga: 2.5, // hatha yoga (Compendium 02150)
  cycling: 8.0, // bicycling 12–13.9 mph (19–22 km/h), moderate effort (Compendium 01030)
  running: 9.8, // running 6 mph (9.7 km/h), 10 min/mile (2011 Compendium 12050)
  ballSports: 7.0, // soccer, casual, general (Compendium 15610)
} as const;
export type Sport = keyof typeof SPORT_MET;
export const SPORTS = Object.keys(SPORT_MET) as Sport[];

/** Awake hours outside walking and sport: sitting, standing, cooking ≈ desk work (1.5 MET). */
const AWAKE_HOURS = 16;
const DAILY_LIFE_MET = 1.5;
/** Walking at a usual 2.8–3.4 mph (≈ 4.8 km/h): 3.8 MET (2024 Compendium 17190). */
const WALK_MET = 3.8;
const WALK_KMH = 4.8;
/** Step length ≈ 0.415 × height (pedometer rule; 0.71 m at 170 cm, adults average 0.67–0.76 m). */
const STEP_PER_HEIGHT = 0.415;
/** Without an answer: 5000 steps, the upper end of "sedentary" (Tudor-Locke 2008, 2013). */
export const DEFAULT_STEPS = 5000;
/** Digestion: ≈ 10 % of daily energy on a mixed diet (Westerterp 2004). */
const TEF = 0.1;

/**
 * `sex`, `steps` (a day) and `sports` (minutes a week per sport) are optional: without a sex the
 * plan sits between the female and male values; without steps it assumes DEFAULT_STEPS.
 */
export type DietProfile = {
  weightKg: number;
  heightCm: number;
  age: AgeRange;
  sex?: Sex;
  steps?: number;
  sports?: Partial<Record<Sport, number>>;
  targetWeightKg: number;
};

/** EU's 14 allergens that must be declared on food labels. */
export const ALLERGENS = [
  'gluten', 'crustaceans', 'eggs', 'fish', 'peanuts', 'soy', 'milk', 'treeNuts', 'celery', 'mustard', 'sesame', 'sulphites', 'lupin', 'molluscs',
] as const;
export type Allergen = (typeof ALLERGENS)[number];

/**
 * Allergens the diet checks already know about: every ingredient declares whether it is
 * gluten-free and dairy-free. The other allergens are not in the ingredient data yet.
 */
export const ALLERGEN_DIETS: Partial<Record<Allergen, Diet>> = { gluten: 'glutenFree', milk: 'dairyFree' };

/** The user's diets plus the diets their allergies imply, in DIETS order. */
export function dietsWithAllergies(diets: Diet[], allergies: Allergen[]): Diet[] {
  const all = new Set([...diets, ...allergies.flatMap(a => ALLERGEN_DIETS[a] ?? [])]);
  return DIETS.filter(d => all.has(d));
}

export const LIMITS = { weightKg: [30, 300], heightCm: [120, 230], steps: [0, 60000], sportMinutes: [0, 2400] } as const;

/** Kilocalories a day for one kilogram a week (7700 kcal per kg of body fat). */
const KCAL_PER_KG = 7700;
const DEFICIT = 500; // ≈ 0.45 kg a week
const SURPLUS = 300; // ≈ 0.27 kg a week; slower, so it is mostly muscle with training
const MIN_KCAL = 1200;

export type Goal = 'lose' | 'keep' | 'gain';

/** Micronutrients the plan recommends; values in `unit`. `max` marks an upper limit (sodium). */
export const PLAN_MICROS = ['calcium', 'iron', 'magnesium', 'potassium', 'zinc', 'vitaminC', 'vitaminD', 'sodium'] as const;
export type PlanMicro = (typeof PLAN_MICROS)[number];

/** Daily energy in kcal, part by part (each rounded up; `total` is the rounded sum before rounding). */
export type EnergyParts = { rest: number; dailyLife: number; walking: number; sport: number; digestion: number; total: number };

export type DietPlan = {
  goal: Goal;
  bmr: number;
  /** Where the maintenance calories come from. */
  energy: EnergyParts;
  /** Calories to keep the current weight. */
  maintenance: number;
  calories: number;
  /** grams */
  protein: number;
  fat: number;
  carbohydrates: number;
  fiber: number;
  /** grams, at most */
  sugar: number;
  /** litres */
  water: number;
  micros: { key: PlanMicro; value: number; unit: 'mg' | 'µg'; max?: boolean }[];
  /** kg a week, signed (negative = losing) */
  weeklyChangeKg: number;
  /** Weeks to the target weight, 0 when keeping. */
  weeks: number;
  bmi: number;
  targetBmi: number;
  /** The target weight is under a healthy BMI (18.5). */
  targetUnderweight: boolean;
  /** The calorie floor kept the deficit smaller than planned. */
  floored: boolean;
  /** Calories per meal slot (30 / 40 / 30). */
  meals: { breakfast: number; lunch: number; dinner: number };
};

// Next whole number; the epsilon keeps float noise (2000.0000001) from adding one. + 0 turns −0 into 0.
const up = (n: number) => Math.ceil(n - 1e-9) + 0;
const bmiOf = (kg: number, cm: number) => kg / (cm / 100) ** 2;

export const goalOf = (p: DietProfile): Goal => (Math.abs(p.targetWeightKg - p.weightKg) < 0.5 ? 'keep' : p.targetWeightKg < p.weightKg ? 'lose' : 'gain');

/** Female and male reference intakes per age range (US DRI), [female, male]. */
const MICROS: Record<Exclude<PlanMicro, 'sodium'>, { unit: 'mg' | 'µg'; byAge: Record<AgeRange, [number, number]> }> = {
  calcium: { unit: 'mg', byAge: { '14-18': [1300, 1300], '19-30': [1000, 1000], '31-50': [1000, 1000], '51-70': [1200, 1000], '71+': [1200, 1200] } },
  iron: { unit: 'mg', byAge: { '14-18': [15, 11], '19-30': [18, 8], '31-50': [18, 8], '51-70': [8, 8], '71+': [8, 8] } },
  magnesium: { unit: 'mg', byAge: { '14-18': [360, 410], '19-30': [310, 400], '31-50': [320, 420], '51-70': [320, 420], '71+': [320, 420] } },
  potassium: { unit: 'mg', byAge: { '14-18': [2300, 3000], '19-30': [2600, 3400], '31-50': [2600, 3400], '51-70': [2600, 3400], '71+': [2600, 3400] } },
  zinc: { unit: 'mg', byAge: { '14-18': [9, 11], '19-30': [8, 11], '31-50': [8, 11], '51-70': [8, 11], '71+': [8, 11] } },
  vitaminC: { unit: 'mg', byAge: { '14-18': [65, 75], '19-30': [75, 90], '31-50': [75, 90], '51-70': [75, 90], '71+': [75, 90] } },
  vitaminD: { unit: 'µg', byAge: { '14-18': [15, 15], '19-30': [15, 15], '31-50': [15, 15], '51-70': [15, 15], '71+': [20, 20] } },
};

/** Recommended daily amounts for the age range and sex; the higher of the two without a sex. */
function micros(age: AgeRange, sex?: Sex): DietPlan['micros'] {
  const pick = ([female, male]: [number, number]) => (sex === 'female' ? female : sex === 'male' ? male : Math.max(female, male));
  return [
    ...Object.entries(MICROS).map(([key, m]) => ({ key: key as PlanMicro, value: pick(m.byAge[age]), unit: m.unit })),
    { key: 'sodium', value: 2300, unit: 'mg', max: true },
  ];
}

/** Unrounded energy parts: rest + what activities add above rest, then digestion on top. */
export function energyParts(p: DietProfile, bmr: number): EnergyParts {
  const perHour = bmr / 24; // 1 MET for this person
  const above = (met: number, hours: number) => (met - 1) * perHour * hours;
  const km = ((p.steps ?? DEFAULT_STEPS) * STEP_PER_HEIGHT * p.heightCm) / 100000;
  const walkHours = km / WALK_KMH;
  const sportHours = Object.values(p.sports ?? {}).reduce((sum, min) => sum + (min ?? 0), 0) / 60 / 7;
  const sport = SPORTS.reduce((sum, x) => sum + above(SPORT_MET[x], (p.sports?.[x] ?? 0) / 60 / 7), 0);
  const walking = above(WALK_MET, walkHours);
  const dailyLife = above(DAILY_LIFE_MET, Math.max(AWAKE_HOURS - walkHours - sportHours, 0));
  const moving = bmr + dailyLife + walking + sport;
  const total = moving / (1 - TEF);
  return { rest: bmr, dailyLife, walking, sport, digestion: total - moving, total };
}

export function dietPlan(p: DietProfile): DietPlan {
  const goal = goalOf(p);
  // Mifflin–St Jeor: +5 for men, −161 for women, averaged to −78 without a sex.
  const bmr = 10 * p.weightKg + 6.25 * p.heightCm - 5 * AGE_MIDDLE[p.age] + (p.sex === 'male' ? 5 : p.sex === 'female' ? -161 : -78);
  const energy = energyParts(p, bmr);
  const maintenance = energy.total;
  const wanted = goal === 'lose' ? maintenance - DEFICIT : goal === 'gain' ? maintenance + SURPLUS : maintenance;
  const floor = Math.max(MIN_KCAL, bmr);
  const calories = goal === 'lose' ? Math.max(wanted, Math.min(floor, maintenance)) : wanted;
  const floored = goal === 'lose' && calories > wanted;

  // Protein by body weight (more while losing, to keep muscle), fat 30 % of calories, carbs the rest.
  const protein = p.weightKg * (goal === 'keep' ? 1.2 : 1.6);
  const fat = (calories * 0.3) / 9;
  const carbohydrates = Math.max(calories - protein * 4 - fat * 9, 0) / 4;

  const weeklyChangeKg = ((calories - maintenance) * 7) / KCAL_PER_KG;
  const weeks = goal === 'keep' || weeklyChangeKg === 0 ? 0 : (p.targetWeightKg - p.weightKg) / weeklyChangeKg;
  const targetBmi = bmiOf(p.targetWeightKg, p.heightCm);
  const kcal = up(calories);

  return {
    goal,
    bmr: up(bmr),
    energy: {
      rest: up(energy.rest), dailyLife: up(energy.dailyLife), walking: up(energy.walking), sport: up(energy.sport), digestion: up(energy.digestion), total: up(energy.total),
    },
    maintenance: up(maintenance),
    calories: kcal,
    protein: up(protein),
    fat: up(fat),
    carbohydrates: up(carbohydrates),
    fiber: up((calories / 1000) * 14),
    sugar: up((calories * 0.1) / 4), // WHO: free sugars under 10 % of calories
    water: up(p.weightKg * 0.035),
    micros: micros(p.age, p.sex),
    weeklyChangeKg: Math.round(weeklyChangeKg * 100) / 100,
    weeks: up(Math.max(weeks, 0)),
    bmi: Math.round(bmiOf(p.weightKg, p.heightCm) * 10) / 10,
    targetBmi: Math.round(targetBmi * 10) / 10,
    targetUnderweight: targetBmi < 18.5,
    floored,
    meals: { breakfast: up(kcal * 0.3), lunch: up(kcal * 0.4), dinner: up(kcal * 0.3) },
  };
}

/** Parses a number typed by the user ("72,5" works); null when it is outside `limits`. */
export function parseMeasure(text: string, [min, max]: readonly [number, number]): number | null {
  const n = Number(text.trim().replace(',', '.'));
  return text.trim() && Number.isFinite(n) && n >= min && n <= max ? n : null;
}
